/**
 * Plain-English records for pair / trio / head-to-head stats.
 *
 * Chemistry, curse and rivalry "scores" blend results with games played so the
 * rankings are fair, but the number itself means nothing to players. UT
 * screens rank by the score and show the record behind it instead.
 */

export interface Record3 {
  wins: number;
  draws: number;
  losses: number;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const games = (r: Record3) => r.wins + r.draws + r.losses;

/** "Won 14 of 20" — for best-pair / best-trio stats. */
export function wonOf(r: Record3): string {
  return `Won ${r.wins} of ${games(r)}`;
}

/** "Lost 12 of 18" — for cursed pairs / trios. */
export function lostOf(r: Record3): string {
  return `Lost ${r.losses} of ${games(r)}`;
}

/** "14 wins · 3 draws · 3 losses" */
export function recordDetail(r: Record3): string {
  return [plural(r.wins, 'win'), plural(r.draws, 'draw'), plural(r.losses, 'loss', 'losses')].join(' · ');
}

/** Compact "14W 3D 3L" for tight table cells. */
export function recordShort(r: Record3): string {
  return `${r.wins}W ${r.draws}D ${r.losses}L`;
}

export interface HeadToHead {
  aId: string;
  aName: string;
  bId: string;
  bName: string;
  /** Games A won against B. */
  aWins: number;
  draws: number;
  /** Games B won against A. */
  bWins: number;
}

/**
 * Orient a head-to-head so the player who leads it comes first, and describe it:
 * headline "Jude won 12 of 18", detail "Dave won 4 · 2 drawn".
 */
export function describeHeadToHead(h: HeadToHead): {
  leaderId: string;
  leaderName: string;
  trailerId: string;
  trailerName: string;
  headline: string;
  short: string;
  detail: string;
} {
  const aLeads = h.aWins >= h.bWins;
  const [leaderId, leaderName, lw, trailerId, trailerName, tw] = aLeads
    ? [h.aId, h.aName, h.aWins, h.bId, h.bName, h.bWins]
    : [h.bId, h.bName, h.bWins, h.aId, h.aName, h.aWins];
  const total = h.aWins + h.draws + h.bWins;
  return {
    leaderId,
    leaderName,
    trailerId,
    trailerName,
    headline: `${leaderName} won ${lw} of ${total}`,
    short: `Won ${lw} of ${total}`,
    detail: `${trailerName} won ${tw} · ${plural(h.draws, 'draw')}`,
  };
}
